import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { capitalizarPalabras } from '../../../common/upperCaseWord';

const SLIDE_CLASS = 'min-w-0 shrink-0 pl-3 flex-[0_0_72%] sm:flex-[0_0_46%] md:flex-[0_0_36%] lg:flex-[0_0_30%]';

export const BannerImageCarousel = ({
    banners = [],
    selectedId,
    onSelect,
    error = null,
    required = false,
}) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        loop: false,
        skipSnaps: false,
        dragFree: true,
        containScroll: 'trimSnaps',
    });

    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const isProgrammaticScrollRef = useRef(false);

    const syncCarouselState = useCallback((api) => {
        setPrevBtnDisabled(!api.canScrollPrev());
        setNextBtnDisabled(!api.canScrollNext());
        setSelectedIndex(api.selectedScrollSnap());

        if (isProgrammaticScrollRef.current) {
            isProgrammaticScrollRef.current = false;
        }
    }, []);

    const scrollPrev = useCallback(() => {
        emblaApi?.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        emblaApi?.scrollNext();
    }, [emblaApi]);

    const scrollToBanner = useCallback((index, programmatic = false) => {
        if (programmatic) {
            isProgrammaticScrollRef.current = true;
        }
        emblaApi?.scrollTo(index);
    }, [emblaApi]);

    const handleSelectBanner = useCallback((banner, index) => {
        scrollToBanner(index, true);
        onSelect?.(banner);
    }, [onSelect, scrollToBanner]);

    useEffect(() => {
        if (!emblaApi) return undefined;

        syncCarouselState(emblaApi);
        emblaApi.on('reInit', syncCarouselState);
        emblaApi.on('select', syncCarouselState);

        return () => {
            try {
                emblaApi?.off?.('reInit', syncCarouselState);
                emblaApi?.off?.('select', syncCarouselState);
            } catch {
                // el carrusel ya se destruyó
            }
        };
    }, [emblaApi, syncCarouselState]);

    useEffect(() => {
        if (!emblaApi || !banners.length || !selectedId) return;
        const index = banners.findIndex((banner) => banner._id === selectedId);
        if (index < 0) return;
        if (emblaApi.selectedScrollSnap() !== index) {
            scrollToBanner(index, true);
        }
        setSelectedIndex(index);
    }, [emblaApi, banners, selectedId, scrollToBanner]);

    if (!banners.length) {
        return (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center">
                <div className="mb-2 rounded-full bg-white p-2.5 shadow-sm ring-1 ring-slate-200">
                    <svg className="h-6 w-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </div>
                <p className="text-sm font-medium text-slate-700">No hay banners disponibles</p>
                <p className="mt-1 max-w-xs text-xs text-slate-500">
                    Agrega imágenes en Ajustes → Imágenes de encabezado para poder asignarlas al curso.
                </p>
            </div>
        );
    }

    const showControls = banners.length > 1;
    const selectedBanner = banners.find((banner) => banner._id === selectedId) || null;
    const selectedPosition = selectedBanner
        ? banners.findIndex((banner) => banner._id === selectedId) + 1
        : 0;

    return (
        <div className="w-full">
            {required ? (
                <p className="mb-2 text-sm font-medium text-slate-700">
                    Imagen de portada
                    <span className="ml-1 text-red-500" aria-hidden="true">*</span>
                </p>
            ) : null}
            <div className={`w-full overflow-hidden rounded-lg border bg-slate-50/50 ${
                error ? 'border-red-400 ring-2 ring-red-100' : 'border-slate-200/90'
            }`}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 bg-white px-3 py-2.5 sm:px-4">
                <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                        Vista previa
                    </p>
                    <p className="truncate text-sm font-medium text-slate-800 capitalize">
                        {selectedBanner
                            ? capitalizarPalabras(selectedBanner.name)
                            : 'Sin banner seleccionado'}
                    </p>
                </div>
                {selectedBanner ? (
                    <span className="inline-flex shrink-0 items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-100">
                        {selectedPosition} de {banners.length}
                    </span>
                ) : (
                    <span className="text-xs text-slate-500">Desliza y elige una imagen</span>
                )}
            </div>

            <div className="overflow-hidden touch-manipulation overscroll-contain py-3 pr-3" ref={emblaRef}>
                <div className="-ml-3 flex">
                    {banners.map((banner, index) => {
                        const isSelected = selectedId === banner._id;
                        return (
                            <div
                                key={banner._id}
                                className={SLIDE_CLASS}
                            >
                                <button
                                    type="button"
                                    onClick={() => handleSelectBanner(banner, index)}
                                    aria-pressed={isSelected}
                                    aria-label={`Seleccionar banner ${banner.name}`}
                                    className={`group w-full overflow-hidden rounded-md bg-white text-left transition-[border-color,box-shadow,opacity,transform] duration-200 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                                        isSelected
                                            ? 'border-2 border-indigo-600 opacity-100 shadow-md shadow-indigo-100/80'
                                            : 'border border-slate-200 opacity-85 hover:-translate-y-0.5 hover:border-slate-300 hover:opacity-100 hover:shadow-sm motion-reduce:hover:translate-y-0'
                                    }`}
                                >
                                    <div className="relative h-[72px] w-full overflow-hidden bg-slate-100 sm:h-[80px]">
                                        <img
                                            src={banner.urlName}
                                            alt={banner.name}
                                            className="h-full w-full object-cover"
                                            loading={index < 3 ? 'eager' : 'lazy'}
                                            decoding="async"
                                        />
                                        {isSelected && (
                                            <span className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm">
                                                <CheckCircleSolidIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                                <span className="sr-only">Seleccionado</span>
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 px-2 py-1.5">
                                        <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-slate-700 capitalize">
                                            {capitalizarPalabras(banner.name)}
                                        </p>
                                    </div>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {showControls && (
                <div className="flex items-center justify-center gap-3 border-t border-slate-200/80 px-3 py-2">
                    <button
                        type="button"
                        className="rounded-full bg-indigo-600 p-1.5 text-white shadow-sm transition-[transform,background-color] duration-200 hover:bg-indigo-700 hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-300 motion-reduce:transition-none motion-reduce:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                        onClick={scrollPrev}
                        disabled={prevBtnDisabled}
                        aria-label="Banner anterior"
                    >
                        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                    </button>

                    <div className="flex items-center gap-1.5" role="tablist" aria-label="Banners disponibles">
                        {banners.map((banner, index) => (
                            <button
                                key={banner._id}
                                type="button"
                                role="tab"
                                aria-selected={selectedIndex === index}
                                aria-label={`Ir al banner ${index + 1}: ${banner.name}`}
                                className={`h-1.5 rounded-full transition-[width,background-color] duration-300 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                                    selectedIndex === index
                                        ? 'w-5 bg-indigo-600'
                                        : 'w-1.5 bg-slate-300 hover:bg-slate-400'
                                }`}
                                onClick={() => scrollToBanner(index)}
                            />
                        ))}
                    </div>

                    <button
                        type="button"
                        className="rounded-full bg-indigo-600 p-1.5 text-white shadow-sm transition-[transform,background-color] duration-200 hover:bg-indigo-700 hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-300 motion-reduce:transition-none motion-reduce:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                        onClick={scrollNext}
                        disabled={nextBtnDisabled}
                        aria-label="Siguiente banner"
                    >
                        <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
            )}
            </div>
            {error ? (
                <p className="mt-2 text-sm text-red-600" role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};
